import { useState } from 'react';
import {
  AritoIcon,
  BulkDeleteDialog,
  ExcelImportDialog,
  ExcelImportErrorDialog,
  ErpDataTableV2
} from '@/components/custom/erp';
import { NhaCungCapProvider, useNhaCungCapContext } from './context';
import { ActionBar, ConfirmDialog, FormDialog } from './components';
import { exportNhaCungCapExcel } from './utils/excel-export';
import { ExcelConfig, ImportBody } from './utils/excel-util';
import { ColumnMapping, ValidationError } from '@/lib/excel';
import { toast, useExcel, useExcelProcessor } from '@/hooks';
import { getTableColumns } from './cols-definition';
import { useAuth } from '@/contexts/auth-context';
import { NhaCungCapInput } from '@/types/schemas';

function NhaCungCapPageContent() {
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errorData, setErrorData] = useState<{
    errors: ValidationError[];
    columnMapping: Record<string, ColumnMapping>;
  } | null>(null);

  const {
    addItem,
    updateItem,
    deleteItem,
    refreshData: refreshNhaCungCaps,
    isLoading,
    currentPage,
    totalItems,
    handlePageChange,
    handleSearchChange,
    tables,
    selectedObj,
    selectedRowIndex,
    showBulkDelete,
    handleCloseBulkDelete,
    handleRowClick,
    clearSelection,
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    isImportDialogOpen,
    setIsImportDialogOpen,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleOpenImportExcel
  } = useNhaCungCapContext();

  const { processFile } = useExcelProcessor(ExcelConfig);
  const { getUUIDByCode } = useExcel();
  const { entity } = useAuth();

  const handleSubmit = async (data: NhaCungCapInput) => {
    if (formMode === 'add') {
      await addItem(data);
    } else if (formMode === 'edit' && selectedObj) {
      await updateItem(selectedObj.uuid, data);
    }

    handleCloseForm();
    clearSelection();
    await refreshNhaCungCaps();
  };

  const handleImportBodies = async (bodies: ImportBody[]) => {
    try {
      for (const body of bodies) {
        await addItem(body as NhaCungCapInput);
      }
      await refreshNhaCungCaps();
      toast({
        description: 'Đã nhập danh mục nhà cung cấp từ Excel thành công',
        variant: 'default'
      });
    } catch (error) {
      console.error('Import error:', error);
      throw error;
    }
  };

  const handleImportFromExcel = async (data: { errors?: string; file: File | null; sheetIndex?: string }) => {
    if (!data.file) {
      toast({
        description: 'Vui lòng chọn file Excel',
        variant: 'destructive'
      });
      return;
    }

    const maxErrors = parseInt(data.errors || '200', 10);
    const sheetIndex = parseInt(data.sheetIndex || '0', 10);

    const result = await processFile({
      file: data.file,
      sheetIndex,
      maxErrors,
      getUUIDByCode,
      context: {}
    });

    if (result.success && result.data) {
      await handleImportBodies(result.data);
      setIsImportDialogOpen(false);
    } else if (result.errors && result.columnMapping) {
      setErrorData({ errors: result.errors, columnMapping: result.columnMapping });
      setShowErrorDialog(true);
    }
  };

  const handleExportExcel = async () => {
    if (!entity?.slug) {
      toast({
        description: 'Không tìm thấy thông tin đơn vị',
        variant: 'destructive'
      });
      return;
    }

    setIsExporting(true);
    try {
      const exportedCount = await exportNhaCungCapExcel(entity.slug);
      if (!exportedCount) {
        toast({
          description: 'Không có dữ liệu để kết xuất',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error exporting suppliers to Excel:', error);
      toast({
        description: 'Có lỗi xảy ra khi kết xuất Excel',
        variant: 'destructive'
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      {showForm && (
        <FormDialog
          open={showForm}
          onSubmit={handleSubmit}
          formMode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? selectedObj
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onClose={handleCloseForm}
          onAdd={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEdit={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDelete={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopy={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      {showDelete && (
        <ConfirmDialog
          open={showDelete}
          onClose={handleCloseDelete}
          selectedObj={selectedObj}
          onDelete={deleteItem}
          clearSelection={clearSelection}
          title='Xoá dữ liệu'
          content='Bạn có chắc chắn xoá không?'
        />
      )}

      {showBulkDelete && (
        <BulkDeleteDialog
          title='Nhà cung cấp'
          onClose={handleCloseBulkDelete}
          columns={getTableColumns(handleViewClick)}
          dataRows={tables[0].rows}
          handleDelete={deleteItem}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAddClick={handleAddClick}
            onEditClick={() => selectedObj && handleEditClick()}
            onCopyClick={() => selectedObj && handleCopyClick()}
            onDeleteClick={() => selectedObj && handleDeleteClick()}
            onViewClick={() => selectedObj && handleViewClick()}
            onRefreshClick={refreshNhaCungCaps}
            onFixedColumnsClick={() => {}}
            onExportClick={handleExportExcel}
            onImportFromExcel={handleOpenImportExcel}
            isViewDisabled={!selectedObj}
            isExporting={isExporting}
          />
          <div data-excel-operation-overlay-root className='min-h-0 flex-1 overflow-hidden'>
            <ErpDataTableV2
              tables={tables}
              selectedRowId={selectedRowIndex || undefined}
              onRowClick={handleRowClick}
              onRowDoubleClick={() => handleViewClick()}
              pageSize={20}
              totalItems={totalItems}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              onSearchChange={handleSearchChange}
              loading={isLoading}
            />
          </div>
        </>
      )}

      {isImportDialogOpen && (
        <ExcelImportDialog
          open={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onSubmit={handleImportFromExcel}
          title='Lấy dữ liệu từ excel'
          titleIcon={<AritoIcon icon={555} />}
        />
      )}

      {showErrorDialog && errorData && (
        <ExcelImportErrorDialog
          open={showErrorDialog}
          onClose={() => {
            setShowErrorDialog(false);
            setErrorData(null);
          }}
          errors={errorData.errors}
          columnMapping={errorData.columnMapping}
        />
      )}
    </div>
  );
}

export default function NhaCungCap() {
  return (
    <NhaCungCapProvider>
      <NhaCungCapPageContent />
    </NhaCungCapProvider>
  );
}
